import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { createModuleLogger } from '@/utils/logger'
import { clearAllAuthParams, getUrlParam, navigateToExternalRedirectWithToken } from '@/utils/url'
import { tokenManager } from '@/utils/token'

const logger = createModuleLogger('ROUTER_GUARDS')
const LOGIN_REDIRECT_STORAGE_KEY = 'tp_login_redirect'

/**
 * 设置路由守卫
 * @param router Vue Router实例
 */
export const setupRouterGuards = (router: Router) => {
  // 全局前置守卫
  router.beforeEach(async(to, from, next) => {
    const authStore = useAuthStore()

    // 方案2：在登录页将 redirect 缓存到 sessionStorage，随后 URL 会在 afterEach 被清理
    if (to.path === '/auth/tenant') {
      const redirect = (to.query?.redirect as string | undefined) || getUrlParam('redirect') || ''
      if (redirect) {
        sessionStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, redirect)
        logger.info('已缓存redirect到sessionStorage', { redirect })
      }
    }

    // 检查URL中是否有code参数（来自控制塔的回调）
    const code = getUrlParam('code')

    if (code) {
      logger.info('检测到URL中的code参数，尝试自动登录', { code: `${code.substring(0, 8)  }...` })

      try {
        // 使用code自动登录（内部会自动清除URL参数）
        await authStore.autoLoginWithCode(code)

        // 如果之前通过 redirect 参数缓存了回跳地址，则在 code 自动登录成功后优先外跳回 VAS
        // （避免只走到 /home?code=... 的默认控制台落地）
        const redirectCached = sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY) || undefined
        const currentToken = tokenManager.get()
        if (redirectCached && currentToken) {
          const jumped = navigateToExternalRedirectWithToken(redirectCached, currentToken)
          if (jumped) {
            sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
            return
          }
        }

        logger.info('Code自动登录成功')
        next()
        return
      } catch (error) {
        logger.error('Code自动登录失败', error)
        next()
        return
      }
    }

    next()
  })

  // 全局后置守卫
  router.afterEach(_to => {
    clearAllAuthParams()
  })
}

